import { useCallback } from 'react';
import { format } from 'date-fns';
import { useHaptics } from './useHaptics';
import { toast } from '@/hooks/use-toast';
import { MealInfo } from '@/components/ui/daily-tile';

export const useShare = () => {
  const { successNotification, errorNotification } = useHaptics();
  const canShare = typeof navigator !== 'undefined' && 'share' in navigator;

  const shareDailyMealPlan = useCallback(async (date: Date, meals: MealInfo[]) => {
    if (!canShare) {
      toast({
        title: "Sharing not supported",
        description: "Your browser doesn't support sharing.",
        variant: "destructive",
      });
      return;
    }

    try {
      const actualMeals = meals.filter(meal => meal.type !== 'note');
      const lunchMeals = actualMeals.filter(meal => meal.mealType === 'lunch');
      const dinnerMeals = actualMeals.filter(meal => meal.mealType === 'dinner');
      const formattedDate = format(date, 'MMMM d, yyyy');
      
      let content = `🍽️ Today's Meal Plan - ${formattedDate}\n\n`;

      if (lunchMeals.length > 0) {
        content += '🥗 Lunch:\n';
        lunchMeals.forEach(meal => {
          content += `• ${meal.name}`;
          if (meal.prepTime && meal.type === 'recipe') {
            content += ` (${meal.prepTime} prep)`;
          }
          content += '\n';
        });
        content += '\n';
      }

      if (dinnerMeals.length > 0) {
        content += '🍖 Dinner:\n';
        dinnerMeals.forEach(meal => {
          content += `• ${meal.name}`;
          if (meal.prepTime && meal.type === 'recipe') {
            content += ` (${meal.prepTime} prep)`;
          }
          content += '\n';
        });
        content += '\n';
      }

      content += 'Planned with Forkast';

      await navigator.share({
        title: `Meal Plan - ${formattedDate}`,
        text: content,
      });

      await successNotification();
      toast({
        title: "Meal plan shared!",
        description: "Your daily meal plan has been shared successfully.",
      });

    } catch (error: any) {
      if (error.name !== 'AbortError') {
        await errorNotification();
        toast({
          title: "Share failed",
          description: "Unable to share your meal plan. Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [canShare, successNotification, errorNotification]);

  const shareWeeklyMealPlans = useCallback(async (weeklyMealPlans: { date: Date; meals: MealInfo[] }[]) => {
    if (!canShare) {
      toast({
        title: "Sharing not supported",
        description: "Your browser doesn't support sharing.",
        variant: "destructive",
      });
      return;
    }

    try {
      const datesWithMeals = weeklyMealPlans.filter(day => 
        day.meals.filter(meal => meal.type !== 'note').length > 0
      );

      if (datesWithMeals.length === 0) {
        toast({
          title: "No meals to share",
          description: "Add some meal plans first to share your weekly plan.",
        });
        return;
      }

      let content = `📅 This Week's Meal Plan\n\n`;

      datesWithMeals.forEach(({ date, meals }) => {
        const formattedDate = format(date, 'EEEE, MMMM d');
        const actualMeals = meals.filter(meal => meal.type !== 'note');
        
        if (actualMeals.length > 0) {
          content += `🗓️ ${formattedDate}:\n`;
          
          const lunchMeals = actualMeals.filter(meal => meal.mealType === 'lunch');
          const dinnerMeals = actualMeals.filter(meal => meal.mealType === 'dinner');
          
          if (lunchMeals.length > 0) {
            content += `🥗 Lunch: ${lunchMeals.map(meal => meal.name).join(', ')}\n`;
          }
          
          if (dinnerMeals.length > 0) {
            content += `🍖 Dinner: ${dinnerMeals.map(meal => meal.name).join(', ')}\n`;
          }
          
          content += '\n';
        }
      });

      content += 'Planned with Forkast';

      await navigator.share({
        title: "This Week's Meal Plan",
        text: content,
      });

      await successNotification();
      toast({
        title: "Meal plan shared!",
        description: "Your weekly meal plan has been shared successfully.",
      });

    } catch (error: any) {
      if (error.name !== 'AbortError') {
        await errorNotification();
        toast({
          title: "Share failed",
          description: "Unable to share your meal plan. Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [canShare, successNotification, errorNotification]);

  const shareInviteCode = useCallback(async (inviteCode: string) => {
    if (!canShare) {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(inviteCode);
        toast({
          title: "Code copied!",
          description: "Invite code copied to clipboard.",
        });
      } catch {
        toast({
          title: "Unable to copy",
          description: "Please copy the code manually.",
          variant: "destructive",
        });
      }
      return;
    }

    try {
      const content = `🏠 Join My Family Circle on Forkast!

I'd love to have you join my family circle so we can plan meals and share recipes together!

Your invite code: ${inviteCode}

1. Download Forkast app
2. Go to Family Circle
3. Enter this code to join

Let's make meal planning easier together!`;

      await navigator.share({
        title: "Join My Family Circle on Forkast",
        text: content,
      });

      await successNotification();
      toast({
        title: "Invite code shared!",
        description: "Your family circle invite code has been shared successfully.",
      });

    } catch (error: any) {
      if (error.name !== 'AbortError') {
        await errorNotification();
        toast({
          title: "Share failed",
          description: "Unable to share your invite code. Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [canShare, successNotification, errorNotification]);

  return {
    shareDailyMealPlan,
    shareWeeklyMealPlans,
    shareInviteCode,
    canShare,
  };
};
